import { useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';

/**
 * Get 1 or 2 widget parts from pasted content.
 * - If "---" on its own line is present: split by that (first block = widget 1, second = widget 2).
 * - Else: find each full <script>...</script> block and use first two (no --- needed).
 */
function getWidgetParts(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return [];
  if (/\n\s*---\s*\n/.test(trimmed)) {
    return trimmed.split(/\n\s*---\s*\n/).map((s) => s.trim()).filter(Boolean);
  }
  const scriptTagRegex = /<\s*script\b[\s\S]*?<\s*\/\s*script\s*>/gi;
  const matches = trimmed.match(scriptTagRegex) || [];
  return matches.slice(0, 2).map((s) => s.trim());
}

/**
 * Runs <script> tags that were added via innerHTML (they don't run automatically).
 */
function runScriptsInElement(container) {
  if (!container) return;
  const scripts = container.querySelectorAll('script');
  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script');
    if (oldScript.src) {
      newScript.src = oldScript.src;
      newScript.async = oldScript.async ?? true;
    } else {
      newScript.textContent = oldScript.textContent;
    }
    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
}

// Selectors for widgets that inject into body (e.g. Trustindex button) so we can move them into our container
const FLOATING_WIDGET_SELECTORS = [
  'iframe[src*="trustindex"]',
  'div[id*="trustindex"]',
  'div[class*="trustindex"]',
  'div[class*="ti-widget"]',
  'div[data-id*="trustindex"]',
  'a[href*="trustindex"]',
];

function findFloatingWidgetInBody() {
  if (!document.body) return null;
  for (const sel of FLOATING_WIDGET_SELECTORS) {
    try {
      const el = document.body.querySelector(sel);
      if (el && el.parentNode === document.body) return el;
    } catch {
      /* ignore invalid selectors */
    }
  }
  return null;
}

function tryMoveFloatingWidgetIntoContainer(containerRef) {
  const widget = findFloatingWidgetInBody();
  if (!widget || !containerRef?.current) return false;
  containerRef.current.appendChild(widget);
  widget.classList.add('excursion-review-widget-contained');
  return true;
}

const ReviewWidgets = ({ reviewWidgetScripts, inline = false }) => {
  const ref1 = useRef(null);
  const ref2 = useRef(null);

  const parts = getWidgetParts(reviewWidgetScripts);
  const firstPart = parts[0] || '';
  const secondPart = parts[1] || '';

  useEffect(() => {
    if (firstPart && ref1.current) {
      ref1.current.innerHTML = firstPart;
      runScriptsInElement(ref1.current);
      // Trustindex (and similar) button widgets inject into body; move into our container after load
      const delays = [500, 1500, 3000];
      const t = delays.map((ms) =>
        setTimeout(() => tryMoveFloatingWidgetIntoContainer(ref1), ms)
      );
      return () => t.forEach(clearTimeout);
    }
  }, [firstPart]);

  useEffect(() => {
    if (secondPart && ref2.current) {
      ref2.current.innerHTML = secondPart;
      runScriptsInElement(ref2.current);
      const delays = [600, 1600, 3200];
      const t = delays.map((ms) =>
        setTimeout(() => tryMoveFloatingWidgetIntoContainer(ref2), ms)
      );
      return () => t.forEach(clearTimeout);
    }
  }, [secondPart]);

  if (parts.length === 0) return null;

  const content = (
    <Row className="g-4">
      {parts[0] && (
        <Col xs={12} md={6}>
          <div ref={ref1} className="excursion-review-widget excursion-review-widget--1 min-h-100" />
        </Col>
      )}
      {parts[1] && (
        <Col xs={12} md={6}>
          <div ref={ref2} className="excursion-review-widget excursion-review-widget--2 min-h-100" />
        </Col>
      )}
    </Row>
  );

  if (inline) {
    return (
      <div className="excursion-review-widgets-inline">
        {content}
      </div>
    );
  }

  return (
    <section id="reviews" className="py-5 py-lg-6 bg-light">
      <Container>
        {content}
      </Container>
    </section>
  );
};

export default ReviewWidgets;
